import { bbxKind } from '../agents/bbx-kind.js';
import { OcrExtractionType } from '../agents/extraction-modules/ocrExtractionTypes.js';
import { type TaskTypeBehaviour } from './task-main.js';

export const gapTextBehaviour = {
  implemented: true,
  structureAgentPrompt:
    'Always create exactly ONE task for the entire gap text passage. Do NOT create subtasks for individual gaps, even if each gap is numbered.',
  examCreationPrompt:
    'Always create exactly ONE task for the entire gap text passage — never one per gap. Wrap ONLY the passage itself (the running text carrying the #gap(...) calls) in #gap-text(...); any instructions or word bank stay outside that wrapper, as plain text in the task body.',
  boundingBox: {
    [bbxKind.STUDENT_ANSWER]: `- Draw EXACTLY ONE single bounding box encompassing the ENTIRE gap text passage as one unit.
- NEVER draw separate bounding boxes around individual gaps.`,
    [bbxKind.MS]: `- Draw EXACTLY ONE single bounding box around the entire master solution text or list.`,
    [bbxKind.GS]: `- Draw EXACTLY ONE single bounding box around the grading criteria for this gap text.`,
    [bbxKind.TASKS]: `- Draw EXACTLY ONE single bounding box encompassing the ENTIRE text passage with the empty gaps.`,
  },
  OCR: {
    [OcrExtractionType.STUDENT_ANSWER]: `- Transcribe ONLY the student's answers for the gaps in strict chronological reading order.
- Output as ONE single numbered Markdown list (e.g., "1. [first gap]"). If blank, use "LEER".`,
    [OcrExtractionType.TASK_DESCRIPTION]: `- Transcribe the entire text passage as ONE single main task with placeholders like "[Lücke 1]".`,
    [OcrExtractionType.MASTER_SOLUTION]: `- Transcribe ONLY the correct answers for the gaps as ONE single numbered Markdown list.`,
    [OcrExtractionType.GRADING_CRITERIA]: `- Extract the grading rules for the entire main task.`,
  },
  generateMS: `- Output ONLY a sequentially numbered list of the correct answers for all gaps.`,
  generateGS: `- Output EXACTLY one bullet point per gap inside the "criteria" JSON field, no introductory prose.
- Use this format per gap (translate label words to the output language defined by "CRITICAL LANGUAGE OVERRIDE"):
    - [GAP_LABEL] [number]: [expected_answer] -> [N] [POINT_LABEL]
  German example:  - Lücke 1: Sonne -> 1 Punkt
  English example: - Gap 1: Sun -> 1 Point`,
  grading: `- GAP TEXT TASK: The JSON "explanation" field MUST contain EXCLUSIVELY a vertical bullet list — one line per gap, in strict chronological order.
- MANDATORY FORMAT for every single gap inside "explanation":
    - [GAP_LABEL] [number]: [student_answer] ([STATUS]) -> [N] [POINT_LABEL]
  Where [STATUS] is exactly one of: [CORRECT_WORD] | [WRONG_WORD] | [EMPTY_WORD]
- TRANSLATE all label words to the output language defined by "CRITICAL LANGUAGE OVERRIDE" below. Never use German labels if the output language is not German.
  Label translation reference:
    German:  Lücke | Richtig | Falsch | Leer | Punkt / Punkte
    English: Gap   | Correct | Wrong  | Empty | Point / Points
    French:  Lacune | Correct | Incorrect | Vide | Point / Points
    Spanish: Espacio | Correcto | Incorrecto | Vacío | Punto / Puntos
  For any other language, translate accordingly.

### CORRECT VALUE FOR "explanation" — German output:
"- Lücke 1: Sonne (Richtig) -> 1 Punkt\\n- Lücke 2: Kälte (Falsch) -> 0 Punkte\\n- Lücke 3: LEER (Leer) -> 0 Punkte"

### CORRECT VALUE FOR "explanation" — English output:
"- Gap 1: Sun (Correct) -> 1 Point\\n- Gap 2: Cold (Wrong) -> 0 Points\\n- Gap 3: EMPTY (Empty) -> 0 Points"

### FORBIDDEN inside "explanation":
  - Prose sentences (e.g. "Der Schüler hat...", "The student answered...")
  - Parenthetical justifications after the point value (e.g. "-> 0 Points (because the correct answer was...)")
  - Any text before the first gap line

- The "feedback" field contains a SHORT overall student-facing summary (1-2 sentences) in the output language. Do NOT repeat the per-gap list there.`,
} satisfies TaskTypeBehaviour;
